import React, { useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import HistoryIcon from '@mui/icons-material/History';
import { GridSearchIcon } from '@mui/x-data-grid';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import i18n from '../../helpers/i18n';
import exportFile from '../../helpers/functions/exportFile';
import { getDeviceHistory } from './DeviceHistoryModal.queries';
import {
  cellSx,
  CloseFooterButton,
  DownloadButton,
  headerCellSx,
  SearchTextField,
  StyledTableRow,
} from './styles';

const formatHistoryDate = (isoDate) => dayjs(isoDate).format('DD/MM/YYYY HH:mm');

const ignoreDiacritics = (str = '') => str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

function DeviceHistoryModal({ open, onClose, deviceName, identity }) {
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [downloadAnchor, setDownloadAnchor] = useState(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['deviceHistory', identity],
    queryFn: () => getDeviceHistory({ identity }),
    enabled: open && !!identity,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const allRows = useMemo(() => data?.data ?? [], [data?.data]);

  const filteredRows = useMemo(() => {
    const term = ignoreDiacritics(searchText.trim().toLowerCase());
    if (!term) return allRows;

    return allRows.filter((row) => {
      const usuario = ignoreDiacritics((row.usuario || '').toLowerCase());
      const observacao = ignoreDiacritics((row.observacao || '').toLowerCase());
      return usuario.includes(term) || observacao.includes(term);
    });
  }, [allRows, searchText]);

  const paginatedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, page, rowsPerPage]);

  const handleClose = () => {
    setSearchText('');
    setPage(0);
    setDownloadAnchor(null);
    onClose();
  };

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
    setPage(0);
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDownload = (type) => {
    const columnsTable = [
      { name: 'data', label: i18n.t('historyModal.columnDate') },
      { name: 'usuario', label: i18n.t('historyModal.columnUser') },
      { name: 'observacao', label: i18n.t('historyModal.columnObservation') },
    ];

    const exportData = filteredRows.map((row) => ({
      data: formatHistoryDate(row.data),
      usuario: row.usuario,
      observacao: row.observacao,
    }));

    exportFile(columnsTable, exportData, type);
    setDownloadAnchor(null);
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="device-history-title"
      aria-describedby="device-history-description"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '824px',
          maxWidth: '90vw',
          borderRadius: '10px',
          boxShadow: 24,
          bgcolor: '#FFFFFF',
          outline: 'none',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            padding: '16px 32px 0',
          }}
        >
          <IconButton
            aria-label={i18n.t('historyModal.close')}
            onClick={handleClose}
            sx={{
              position: 'absolute',
              top: 8,
              right: 16,
              color: 'var(--ahg-waterloo-base, #78788F)',
            }}
          >
            <CloseIcon />
          </IconButton>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', mb: 2, pr: 4 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                bgcolor: 'var(--ahg-primary-base, #1199DD)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <HistoryIcon sx={{ color: '#FFFFFF', fontSize: 22 }} />
            </Box>
            <Box>
              <Typography
                id="device-history-title"
                sx={{
                  fontFamily: 'Open Sans',
                  fontSize: '24px',
                  fontWeight: 600,
                  lineHeight: '32px',
                  color: 'rgba(0, 0, 0, 0.87)',
                }}
              >
                {i18n.t('historyModal.title')}
              </Typography>
              <Typography
                id="device-history-description"
                sx={{
                  fontFamily: 'Open Sans',
                  fontSize: '14px',
                  fontWeight: 400,
                  lineHeight: '20px',
                  color: 'var(--ahg-waterloo-base, #78788F)',
                }}
              >
                {deviceName || ''}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: 2,
              mb: 1.5,
            }}
          >
            <SearchTextField
              fullWidth
              variant="standard"
              value={searchText}
              onChange={handleSearchChange}
              placeholder={i18n.t('historyModal.searchPlaceholder')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ mr: 0.5 }}>
                    <GridSearchIcon sx={{ color: 'var(--ahg-waterloo-base, #78788F)', fontSize: 20 }} />
                  </InputAdornment>
                ),
                disableUnderline: false,
              }}
            />
            <DownloadButton
              startIcon={<CloudDownloadIcon sx={{ fontSize: 22 }} />}
              onClick={(event) => setDownloadAnchor(event.currentTarget)}
              disabled={filteredRows.length === 0}
            >
              {i18n.t('historyModal.download')}
            </DownloadButton>
            <Menu
              anchorEl={downloadAnchor}
              open={Boolean(downloadAnchor)}
              onClose={() => setDownloadAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem onClick={() => handleDownload('csv')}>
                {i18n.t('toolBar.export.csv')}
              </MenuItem>
              <MenuItem onClick={() => handleDownload('xlsx')}>
                {i18n.t('toolBar.export.xlsx')}
              </MenuItem>
            </Menu>
          </Box>

          <TableContainer sx={{ overflow: 'visible' }}>
            <Table size="small" sx={{ tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headerCellSx, width: '22%' }}>{i18n.t('historyModal.columnDate')}</TableCell>
                  <TableCell sx={{ ...headerCellSx, width: '28%' }}>{i18n.t('historyModal.columnUser')}</TableCell>
                  <TableCell sx={headerCellSx}>{i18n.t('historyModal.columnObservation')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading && (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                )}
                {!isLoading && isError && (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 4, ...cellSx }}>
                      {i18n.t('historyModal.loadError')}
                    </TableCell>
                  </TableRow>
                )}
                {!isLoading && !isError && paginatedRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 4, ...cellSx }}>
                      {i18n.t('historyModal.empty')}
                    </TableCell>
                  </TableRow>
                )}
                {!isLoading && !isError && paginatedRows.map((row, index) => (
                  <StyledTableRow key={`${row.data}-${row.usuario}-${index}`}>
                    <TableCell sx={cellSx}>{formatHistoryDate(row.data)}</TableCell>
                    <TableCell sx={cellSx}>{row.usuario}</TableCell>
                    <TableCell sx={cellSx}>{row.observacao}</TableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {!isLoading && !isError && filteredRows.length > 0 && (
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50]}
              labelRowsPerPage={i18n.t('historyModal.labelRowsPerPage')}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} ${i18n.t('historyModal.of')} ${count}`
              }
              sx={{
                minHeight: '40px',
                borderTop: 'none',
                '& .MuiTablePagination-toolbar': {
                  minHeight: '40px',
                  paddingLeft: 0,
                  paddingRight: 0,
                },
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  fontFamily: 'Open Sans',
                  fontSize: '14px',
                  color: 'var(--ahg-waterloo-darken1)',
                },
              }}
            />
          )}
        </Box>

        <Box
          sx={{
            padding: '12px 32px 20px',
            borderTop: '1px solid var(--ahg-gray-lighten4, #E7E7EB)',
          }}
        >
          <CloseFooterButton variant="contained" color="primary" onClick={handleClose}>
            {i18n.t('historyModal.close')}
          </CloseFooterButton>
        </Box>
      </Box>
    </Modal>
  );
}

export default DeviceHistoryModal;
